import { faHouse, faPaw, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createStyles, makeStyles, Typography } from '@material-ui/core';
import { showSuccessThunk } from '@spike/notifications-action';
import {
    clearServiceSessionsWarnings,
    saveSpecificSessionsThunk
} from 'actions/serviceSessions/ServiceSessionsActions';
import Button from 'components/UI/V2/Button/Button';
import { Service } from 'model/Service';
import Staff from 'model/Staff';
import moment, { Moment } from 'moment-timezone';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import {
    buildSpecificAssignments,
    dateKey,
    draftsMatchSessions,
    duplicateAssignmentKeys,
    getCancellationsForDate,
    getSessionsForDate,
    getSessionTypeLabel,
    getServiceSessionConfig,
    getWeeklySessionsForWeekDay,
    isSameSessionAssignment,
    warningsForActiveAssignments,
    weekDayOf,
    SessionAssignmentDraft,
    ServiceSession,
    SessionZone,
    SESSION_DUPLICATE_SLOT_MESSAGE,
    SESSION_ZONE_REQUIRED_MESSAGE,
    SessionBusinessArea
} from 'model/ServiceSession';
import { MultiSlotDatePicker } from '../MultiSlotDrawer/DatePicker';
import { MultiSlotDrawerBody } from '../MultiSlotDrawer/MultiSlotDrawerBody';
import { MultiSlotDrawerFooter } from '../MultiSlotDrawer/MultiSlotDrawerFooter';
import { MultiSlotDrawerHeader } from '../MultiSlotDrawer/MultiSlotDrawerHeader';
import { useSessionHourOptions } from './useSessionHourOptions';
import { SessionConflictWarnings, SessionScheduleAlert } from './SessionConflictWarnings';
import {
    SessionCancelledSlotRow,
    SessionCancelledSlots,
    SessionStaffSlotRow,
    SessionStaffSlots
} from './SessionStaffSlotRow';
import { ZoneSelectionPanel } from './ZoneSelectionPanel';

export interface SessionAddSlotDrawerProps {
    area: SessionBusinessArea;
    service: Service;
    date: Moment;
    dates: Array<Moment>;
    staff: Array<Staff>;
    onZonePanelChange: (open: boolean) => void;
    onClose: () => void;
}

interface DraftAssignment {
    key: string;
    staffId?: number;
    time?: string;
    zone?: SessionZone;
}

const useStyles = makeStyles(() =>
    createStyles({
        serviceCard: {
            gap: 6,
            display: 'flex',
            padding: '14px 16px',
            borderRadius: 12,
            flexDirection: 'column',
            alignItems: 'flex-start',
            border: 'solid 1px #D4D4D4'
        },
        serviceChip: {
            gap: 5,
            fontSize: 12,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            padding: '2px 11px',
            borderRadius: 60,
            backgroundColor: '#E4DDD5'
        },
        serviceName: {
            gap: 8,
            fontSize: 16,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center'
        },
        petsBadge: {
            gap: 4,
            fontSize: 12,
            display: 'flex',
            padding: '2px 8px',
            borderRadius: 60,
            alignItems: 'center',
            backgroundColor: '#F1F1F1'
        },
        serviceSubtitle: {
            fontSize: 14,
            color: '#7A7A7A'
        },
        addStaffButton: {
            alignSelf: 'flex-start'
        }
    })
);

let draftKey = 0;
const newAssignment = (): DraftAssignment => ({ key: `date-assignment-${draftKey++}` });

export const SessionAddSlotDrawer: React.FC<SessionAddSlotDrawerProps> = props => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const days = useSelector((state: RootState) => state.serviceSessions.days);
    const saving = useSelector((state: RootState) => state.serviceSessions.saving);
    const warnings = useSelector((state: RootState) => state.serviceSessions.warnings);
    const assignmentErrors = useSelector((state: RootState) => state.serviceSessions.assignmentErrors);

    const [selectedDate, setSelectedDate] = React.useState<Moment>(props.date);
    const [assignments, setAssignments] = React.useState<Array<DraftAssignment>>([]);
    const [editingZoneKey, setEditingZoneKey] = React.useState<string>();
    const [showZoneErrors, setShowZoneErrors] = React.useState(false);

    const config = getServiceSessionConfig(props.service, props.area);
    const hourOptions = useSessionHourOptions();
    const isPastDate = selectedDate.isBefore(moment(), 'day');

    React.useEffect(() => {
        dispatch(clearServiceSessionsWarnings());
    }, []);

    React.useEffect(() => {
        const sessions = getSessionsForDate(days, props.service.id!, selectedDate);

        const cancellations = getCancellationsForDate(days, props.service.id!, selectedDate);

        setAssignments(
            sessions.length > 0
                ? sessions.map(session => ({
                      key: session.uuid,
                      staffId: session.staffId,
                      time: session.time,
                      zone: session.zone
                  }))
                : isPastDate || cancellations.length > 0
                ? []
                : [newAssignment()]
        );
        setShowZoneErrors(false);
    }, [selectedDate.format('YYYY-MM-DD'), props.service.id, days]);

    const changeDateHandler = (value: string) => {
        dispatch(clearServiceSessionsWarnings());
        setShowZoneErrors(false);
        setSelectedDate(moment(value, 'YYYY-MM-DD'));
    };

    const changeAssignmentHandler = (key: string, values: { staffId?: number; time?: string }) => {
        dispatch(clearServiceSessionsWarnings());
        setShowZoneErrors(false);
        setAssignments(prev => prev.map(item => (item.key === key ? { ...item, ...values } : item)));
    };

    const editZoneHandler = (key?: string) => {
        setEditingZoneKey(key);
        props.onZonePanelChange(!!key);
    };

    const saveZoneHandler = (zone: SessionZone) => {
        dispatch(clearServiceSessionsWarnings());
        setShowZoneErrors(false);
        setAssignments(prev => prev.map(item => (item.key === editingZoneKey ? { ...item, zone } : item)));
        editZoneHandler(undefined);
    };

    const removeZoneHandler = (key: string) => {
        dispatch(clearServiceSessionsWarnings());
        setShowZoneErrors(false);
        setAssignments(prev => prev.map(item => (item.key === key ? { ...item, zone: undefined } : item)));
    };

    const originalSessions = React.useMemo(
        () => getSessionsForDate(days, props.service.id!, selectedDate),
        [days, props.service.id, selectedDate]
    );

    const dateCancellations = React.useMemo(
        () => getCancellationsForDate(days, props.service.id!, selectedDate),
        [days, props.service.id, selectedDate]
    );

    const cancelledSessions = React.useMemo(() => {
        return dateCancellations.filter(session => !assignments.some(item => isSameSessionAssignment(item, session)));
    }, [dateCancellations, assignments]);

    const duplicateKeys = React.useMemo(() => duplicateAssignmentKeys(assignments), [assignments]);

    const errorFor = (key: string, staffId?: number, time?: string, zone?: SessionZone): string | undefined => {
        if (duplicateKeys.has(key)) return SESSION_DUPLICATE_SLOT_MESSAGE;
        if (!staffId || !time) return undefined;
        if (showZoneErrors && config.inHome && !zone) return SESSION_ZONE_REQUIRED_MESSAGE;
        return assignmentErrors.find(error => isSameSessionAssignment(error, { staffId, time }))?.messages[0];
    };

    const restoreHandler = (session: ServiceSession) => {
        if (!session.staffId) return;
        dispatch(clearServiceSessionsWarnings());
        setAssignments(prev => {
            if (prev.some(item => isSameSessionAssignment(item, session))) return prev;

            // Cancellation records carry no coverage, so take it back from the weekly they shadow.
            const weekly = getWeeklySessionsForWeekDay(days, props.service.id!, weekDayOf(selectedDate)).find(item =>
                isSameSessionAssignment(item, session)
            );

            return [
                ...prev.filter(item => item.staffId || item.time),
                {
                    key: session.uuid,
                    staffId: session.staffId,
                    time: session.time,
                    zone: session.zone ?? weekly?.zone
                }
            ];
        });
    };

    const completedAssignments = assignments.filter(item => item.staffId && item.time);
    const desired: Array<SessionAssignmentDraft> = completedAssignments.map(item => ({
        staffId: item.staffId!,
        time: item.time!,
        zone: item.zone
    }));
    const isModified = !draftsMatchSessions(desired, originalSessions);
    const canSave = isModified && duplicateKeys.size === 0;
    const activeStaffNames = completedAssignments.flatMap(item => {
        const member = props.staff.find(staff => staff.id === item.staffId);
        return member ? [`${member.person.firstName} ${member.person.lastName}`] : [];
    });

    const saveHandler = () => {
        if (isPastDate || !canSave) return;

        if (config.inHome && completedAssignments.some(assignment => !assignment.zone)) {
            dispatch(clearServiceSessionsWarnings());
            setShowZoneErrors(true);
            return;
        }

        dispatch(
            saveSpecificSessionsThunk(
                props.service.id!,
                dateKey(selectedDate),
                buildSpecificAssignments(
                    desired,
                    getWeeklySessionsForWeekDay(days, props.service.id!, weekDayOf(selectedDate)),
                    dateCancellations
                ),
                saveWarnings => {
                    const visibleWarnings = warningsForActiveAssignments(saveWarnings, activeStaffNames);
                    dispatch(
                        showSuccessThunk(
                            visibleWarnings.length > 0
                                ? `Sessions updated for ${selectedDate.format('MMM D')} with warnings`
                                : `Sessions updated for ${selectedDate.format('MMM D')}`
                        )
                    );
                    if (visibleWarnings.length === 0) props.onClose();
                }
            )
        );
    };

    return (
        <>
            <MultiSlotDrawerHeader
                title="Add Time Slot"
                onClose={props.onClose}
            >
                <>
                    <div className={classes.serviceCard}>
                        {config.inHome && (
                            <span className={classes.serviceChip}>
                                <FontAwesomeIcon icon={faHouse} />
                                Home Service
                            </span>
                        )}

                        <Typography className={classes.serviceName}>
                            {props.service.name}
                            <span className={classes.petsBadge}>
                                <FontAwesomeIcon icon={faPaw} />
                                {config.maxPets}
                            </span>
                        </Typography>

                        <Typography className={classes.serviceSubtitle}>
                            {getSessionTypeLabel(config)}
                            {props.service.olbEnabled ? ' · Online Booking' : ''}
                        </Typography>
                    </div>

                    <MultiSlotDatePicker<string>
                        options={props.dates.map(date => ({
                            label: date.format('ddd, DD'),
                            value: dateKey(date)
                        }))}
                        selectedOption={dateKey(selectedDate)}
                        onDateChange={changeDateHandler}
                    />
                </>
            </MultiSlotDrawerHeader>

            <MultiSlotDrawerBody>
                {isPastDate && (
                    <SessionScheduleAlert>
                        <Typography>You can&apos;t make changes to slots from past dates.</Typography>
                    </SessionScheduleAlert>
                )}

                {assignments.length > 0 && (
                    <SessionStaffSlots
                        showZone={config.inHome}
                        showDelete={!isPastDate}
                    >
                        {assignments.map(assignment => (
                            <SessionStaffSlotRow
                                key={assignment.key}
                                area={props.area}
                                staff={props.staff}
                                hours={hourOptions}
                                time={assignment.time}
                                zone={assignment.zone}
                                staffId={assignment.staffId}
                                showZone={config.inHome}
                                readOnly={isPastDate}
                                error={errorFor(assignment.key, assignment.staffId, assignment.time, assignment.zone)}
                                onEditZone={() => editZoneHandler(assignment.key)}
                                onRemoveZone={() => removeZoneHandler(assignment.key)}
                                onChange={values => changeAssignmentHandler(assignment.key, values)}
                                onDelete={() =>
                                    setAssignments(prev => prev.filter(item => item.key !== assignment.key))
                                }
                            />
                        ))}
                    </SessionStaffSlots>
                )}

                {cancelledSessions.length > 0 && (
                    <SessionCancelledSlots>
                        {cancelledSessions.map(session => {
                            const member = props.staff.find(item => item.id === session.staffId);
                            const staffName =
                                session.staffName ||
                                (member ? `${member.person.firstName} ${member.person.lastName}` : 'Staff');

                            return (
                                <SessionCancelledSlotRow
                                    key={session.uuid}
                                    area={props.area}
                                    time={session.time}
                                    zone={session.zone}
                                    staffName={staffName}
                                    showZone={config.inHome}
                                    onRestore={isPastDate ? undefined : () => restoreHandler(session)}
                                />
                            );
                        })}
                    </SessionCancelledSlots>
                )}

                <SessionConflictWarnings
                    staff={props.staff}
                    date={selectedDate}
                    extraWarnings={warnings}
                    assignments={completedAssignments}
                />

                {!isPastDate && (
                    <Button
                        variant="primary"
                        label="Add Staff"
                        leftIcon={faPlus}
                        id={`${props.area}-slot-add-staff-button`}
                        className={classes.addStaffButton}
                        onClick={() => setAssignments(prev => [...prev, newAssignment()])}
                    />
                )}
            </MultiSlotDrawerBody>

            {!isPastDate && (
                <MultiSlotDrawerFooter>
                    <Button
                        noColorOnDisabled
                        variant="green"
                        label="Save"
                        disabled={!canSave || saving}
                        isLoading={saving}
                        id={`${props.area}-slot-save-button`}
                        onClick={saveHandler}
                    />
                </MultiSlotDrawerFooter>
            )}

            <ZoneSelectionPanel
                area={props.area}
                open={!!editingZoneKey}
                zone={assignments.find(item => item.key === editingZoneKey)?.zone}
                onSave={saveZoneHandler}
                onClose={() => editZoneHandler(undefined)}
            />
        </>
    );
};
